import { Throwable } from "@wss/error-tracking/throwable";
import { toError } from "@wss/error-tracking/utils";
import { createContext } from "@wss/react";
import React, { Dispatch, PropsWithChildren, SetStateAction, useEffect, useState } from "react";
import { GoogleModelViewer, GoogleModelViewerProps, Poster, ProgressBar } from "./direct-import";
let loaded = false;
const subs = new Set<(loaded: true) => void>();
const [Context, useContext] = createContext<[boolean, Dispatch<SetStateAction<boolean>>]>();
function ModelViewerContextProvider({
  children
}: PropsWithChildren) {
  const state = useState<boolean>(loaded);
  return <Context.Provider value={state} data-sentry-element="unknown" data-sentry-component="ModelViewerContextProvider" data-sentry-source-file="index.tsx">{children}</Context.Provider>;
}

/** Will display it's children while the model viewer is loading the model */
function Loading({
  children
}: PropsWithChildren): JSX.Element {
  const [isLoaded] = useContext();
  return <>{!isLoaded ? children : null}</>;
}

/** Will display it's children while the model viewer has finished loading the model */
function Loaded({
  children
}: PropsWithChildren): JSX.Element {
  const [isLoaded] = useContext();
  return <>{isLoaded ? children : null}</>;
}
function ModelViewer({
  children,
  onLoad,
  ...rest
}: PropsWithChildren<Omit<GoogleModelViewerProps, "children">>) {
  const [isLazyLoaded, setIsLazyLoaded] = useState<boolean>(loaded);
  const [, setIsLoaded] = useContext();
  function handleModelLoad(this: HTMLElement, ...args: Parameters<NonNullable<GoogleModelViewerProps["onPosterDismissed"]>>) {
    // This is not the best because it assumes that the consumer is going to care when we have loaded the model
    setIsLoaded(true);
    if (rest.onPosterDismissed) {
      rest.onPosterDismissed.bind(this)(...args);
    }
  }
  useEffect(function initLoadModelViewerSub() {
    subs.add(setIsLazyLoaded);
    return function cleanupLoadModelViewerSub() {
      subs.delete(setIsLazyLoaded);
    };
  }, []);
  return <>
      {isLazyLoaded ? <GoogleModelViewer {...rest} onLoad={onLoad} onPosterDismissed={handleModelLoad}>
          {children}
        </GoogleModelViewer> : null}
    </>;
}

/**
 * The 3D model viewer that is loaded when `LazyLoadedGoogleModelViewer.load` is called.
 *
 * @see {@link GoogleModelViewer Base Component}
 *
 * @param {Object} props - All the props for the `<model-viewer>`
 */
export function LazyLoadedGoogleModelViewer({
  children
}: PropsWithChildren): JSX.Element {
  return <ModelViewerContextProvider data-sentry-element="ModelViewerContextProvider" data-sentry-component="LazyLoadedGoogleModelViewer" data-sentry-source-file="index.tsx">{children}</ModelViewerContextProvider>;
}

/** Will load the module that allows the @google/model-viewer to render.
 *
 * If you need the viewer loaded immediately then import "@wss/model-viewer/direct-import" instead.
 */
LazyLoadedGoogleModelViewer.load = async function handleLoadGoogleModelViewer(): Promise<Throwable> {
  /* The model viewer package is large and should only be brought in when needed. */
  try {
    await import("@google/model-viewer/dist/model-viewer");
    loaded = true;
    subs.forEach(cb => cb(true));
    return {
      isError: false
    };
  } catch (error) {
    return {
      isError: true,
      error: toError(error)
    };
  }
};
LazyLoadedGoogleModelViewer.ModelViewer = ModelViewer;
/** @see {@link Loading} */
LazyLoadedGoogleModelViewer.Loading = Loading;
/** @see {@link Loaded} */
LazyLoadedGoogleModelViewer.Loaded = Loaded;
/** @see {@link Poster} */
LazyLoadedGoogleModelViewer.Poster = Poster;
/** @see {@link ProgressBar} */
LazyLoadedGoogleModelViewer.ProgressBar = ProgressBar;